import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Provides access to the current theme and toggle function from the theme context.
 *
 * @returns The current theme and a function to toggle between 'light' and 'dark' modes.
 *
 * @throws {Error} If called outside of a {@link ThemeProvider}.
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Provides theme state and a toggle function to its child components.
 *
 * Initializes the theme based on a saved user preference or the system's color scheme, and applies the theme by updating the document's class list and local storage. Child components can access the current theme and toggle it using the context.
 *
 * @param children - React nodes that will have access to the theme context.
 *
 * @remark This provider must wrap components that use the {@link useTheme} hook.
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  // Check if user has previously set a theme preference
  const storedTheme = typeof window !== 'undefined' ? localStorage.getItem('theme') as Theme : null;
  const prefersDark = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)').matches : false;
  
  // Initialize theme based on stored preference or system preference
  const [theme, setTheme] = useState<Theme>(storedTheme || (prefersDark ? 'dark' : 'light'));

  // Apply theme to document
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const root = window.document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [theme]);

  // Toggle between light and dark themes
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
